import { endOfExpiryDay } from './batch-expiry.js';
import { isDefined } from './functions.js';
import { getPreparationExpiry } from './stability-expiry.js';
// The events that hand the bag on towards the patient; refused once the use-by has passed (CY-327).
export const USE_BY_GUARDED_EVENTS = ['RELEASE_FOR_DELIVERY', 'ADMINISTER'];
export function isUseByGuardedEvent(type) {
    return USE_BY_GUARDED_EVENTS.includes(type);
}
/** When production was completed: the newest COMPLETE_PRODUCTION status change, as on the label. */
export function getProducedAt(logs) {
    const completions = logs.filter(log => log.details?.action === 'COMPLETE_PRODUCTION').map(log => log.performedAt);
    return completions.length ? Math.max(...completions) : null;
}
function uniqueTradeNames(batches) {
    return [
        ...new Map(batches.flatMap(batch => (batch.tradeName ? [[batch.tradeName.id, batch.tradeName]] : []))).values(),
    ];
}
// the rules assigned to the trade name itself (CY-412)
function collectRules(tradeNames) {
    return tradeNames.flatMap(tradeName => (tradeName.stabilityRules ?? []).map(link => link.stabilityRule).filter(isDefined));
}
/** Use-by of a produced preparation, gathered from the batches picked for it; null before production or when nothing applies. */
export function getSequenceUseBy(source, statusLogs) {
    const producedAt = getProducedAt(statusLogs);
    if (producedAt === null)
        return null;
    const item = source.order?.distributedOrders?.orders?.find(order => order.sequenceId === source.id);
    const batchesById = new Map((source.batches ?? []).flatMap(link => (link.batch ? [[link.batch.id, link.batch]] : [])));
    const pick = (ids) => ids.map(id => (id ? batchesById.get(id) : undefined)).filter(isDefined);
    // rest vials are drug material, so they count with the drug batches
    const drugBatches = pick([
        ...(item?.activeSubstances?.batches.map(batch => batch.batchId) ?? []),
        ...(item?.restVials?.map(restVial => restVial.batchId) ?? []),
    ]);
    const carrierBatches = pick(item?.carrier?.batches.map(batch => batch.batchId) ?? []);
    const drugTradeNames = uniqueTradeNames(drugBatches);
    const carrierTradeNames = uniqueTradeNames(carrierBatches);
    const useBy = getPreparationExpiry({
        referenceTime: producedAt,
        drugRules: collectRules(drugTradeNames),
        carrierRules: collectRules(carrierTradeNames),
        // a vial is usable through its expiry day, so the cap is the end of that day (CY-371)
        drugBatchExpiries: drugBatches.map(batch => batch.expiryDate).filter(isDefined).map(endOfExpiryDay),
        carrierBatchExpiries: carrierBatches.map(batch => batch.expiryDate).filter(isDefined).map(endOfExpiryDay),
        returnExpiries: (item?.returns ?? []).flatMap(ret => (ret.expiryDate ? [new Date(ret.expiryDate).getTime()] : [])),
        drugIsDrySubstance: drugTradeNames.some(tradeName => tradeName.medicationProfile?.isDrySubstance),
        carrierIsDrySubstance: carrierTradeNames.some(tradeName => tradeName.medicationProfile?.isDrySubstance),
    });
    return useBy ?? null;
}
/** Expired from the use-by moment on; a preparation without a use-by is never expired. */
export function isPreparationExpired(useBy, now) {
    return useBy != null && useBy <= now;
}
export class PreparationExpiredError extends Error {
    internalId;
    useBy;
    constructor(internalId, useBy) {
        super(`Preparation ${internalId ?? ''} expired at ${new Date(useBy).toISOString()}: it cannot be released for delivery or administered.`);
        this.internalId = internalId;
        this.useBy = useBy;
        this.name = 'PreparationExpiredError';
    }
}
export function assertPreparationNotExpired(input) {
    if (!isUseByGuardedEvent(input.eventType))
        return;
    if (input.useBy != null && isPreparationExpired(input.useBy, input.now)) {
        throw new PreparationExpiredError(input.internalId, input.useBy);
    }
}
//# sourceMappingURL=preparation-use-by.js.map